import React, { useEffect, useCallback, useRef, useState } from "react";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { useHoldAndWinCollectionController } from "../../../../Hooks/useHoldAndWinCollectionController";
import {
  selectUserInterfaceDisabled,
  selectHoldAndWinIsSpinning,
  selectSpacebarSpinEnabled,
  selectShowHoldAndWinCollector,
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectHoldAndWinActive,
} from "../../../../State/selectors";
import store from "../../../../State/Store/store";
import {
  createButtonPressAnimation,
  createPlayButtonRotationAnimation,
} from "../../../../utilities";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Hold and win play button configuration - matches UI button dimensions
const HOLD_AND_WIN_PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.9,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.675,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Simple action tracking
let lastActionTime = 0;
const MIN_ACTION_GAP = 25;

// Spacebar state tracking (simple tap only, no hold)
let spacePressed = false;

/**
 * Simplified play/stop button for HoldAndWin feature game
 *
 * Features:
 * - Simple spin/stop functionality (no turbo, autoplay, or hold)
 * - Same visual styling and effects as regular play button
 * - Uses HoldAndWin-specific states and controls
 */
const ButtonHoldAndWinPlay = ({ toolbar }) => {
  const [actionInProgress, setActionInProgress] = useState(false);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);
  const { playButtonRef } = useReelRefs();
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig } =
    useDimensions();

  // Refs for latest state access in event handlers
  const setActionInProgressRef = useRef(setActionInProgress);
  const setStopButtonDisabledRef = useRef(setStopButtonDisabled);

  const { handleSpin, handleManualStop } = useHoldAndWinControl();
  const { triggerCollectionSkip } = useHoldAndWinCollectionController();

  // Refs for latest values
  const handleSpinRef = useRef(handleSpin);
  const handleManualStopRef = useRef(handleManualStop);
  const actionInProgressRef = useRef(actionInProgress);
  const stopButtonDisabledRef = useRef(stopButtonDisabled);

  // Update refs when values change
  useEffect(() => {
    handleSpinRef.current = handleSpin;
    handleManualStopRef.current = handleManualStop;
    actionInProgressRef.current = actionInProgress;
    stopButtonDisabledRef.current = stopButtonDisabled;
    setActionInProgressRef.current = setActionInProgress;
    setStopButtonDisabledRef.current = setStopButtonDisabled;
  }, [
    handleSpin,
    handleManualStop,
    actionInProgress,
    stopButtonDisabled,
    setActionInProgress,
    setStopButtonDisabled,
  ]);

  // Redux state selectors
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinIsSpinning = useSelector(selectHoldAndWinIsSpinning);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const spacebarSpinEnabled = useSelector(selectSpacebarSpinEnabled);
  const showHoldAndWinCollector = useSelector(selectShowHoldAndWinCollector);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);

  // Determine current effective speed (pending takes precedence if set)
  const currentSpeed = pendingAutoPlaySpeed || autoPlaySpeed;
  const isTurboMode = currentSpeed === "turbo";

  // Track previous spinning state to detect transitions
  const prevIsSpinningRef = useRef(holdAndWinIsSpinning);

  // Reset button state when Hold and Win feature starts (ensures clean state for each session)
  useEffect(() => {
    if (holdAndWinActive) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      prevIsSpinningRef.current = false;
    }
  }, [holdAndWinActive]);

  // Reset action states when spin completes (transition from spinning to not spinning)
  useEffect(() => {
    // Detect transition from spinning to not spinning
    if (prevIsSpinningRef.current && !holdAndWinIsSpinning) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
    }

    // Update previous state
    prevIsSpinningRef.current = holdAndWinIsSpinning;
  }, [holdAndWinIsSpinning]);

  /**
   * Resolve what action the button should perform
   * Hold and Win uses autoplay - only allow stopping, not starting
   */
  const resolveButtonAction = useCallback(() => {
    const state = store.getState();

    // Stop spin if spinning
    if (state.spin.holdAndWinIsSpinning) {
      return {
        type: "STOP_SPIN",
      };
    }

    // Don't allow manual start - Hold and Win uses autoplay
    return {
      type: "DISABLED",
    };
  }, []);

  /**
   * Execute stop spin action
   */
  const executeStopSpin = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    lastActionTime = Date.now();
    setActionInProgressRef.current(true);
    setStopButtonDisabledRef.current(true);
    if (handleManualStopRef.current) {
      handleManualStopRef.current();
    }
    return true;
  }, []);

  /**
   * Execute start spin action
   * HoldAndWin is a free feature round - no balance/bet checks needed
   */
  const executeStartSpin = useCallback(() => {
    soundManager.play(SOUND_NAME.spin);
    lastActionTime = Date.now();
    setActionInProgress(true);
    if (handleSpinRef.current) {
      handleSpinRef.current();
    }
    return true;
  }, [setActionInProgress]);

  /**
   * Flash brightness effect on click/tap (visual feedback)
   */
  const flashBrightness = useCallback(() => {
    if (!playButtonRef.current) return;

    // Apply brightness filter
    playButtonRef.current.filters = [brightnessFilter.current];

    // Remove after short delay (unless hovering)
    setTimeout(() => {
      if (playButtonRef.current && !isHoveredRef.current) {
        playButtonRef.current.filters = [];
      }
    }, 120);
  }, [playButtonRef]);

  /**
   * Master action executor
   */
  const executeButtonAction = useCallback(
    (action, source = "click") => {
      // Don't do anything if button is disabled
      if (action.type === "DISABLED") {
        return;
      }

      // Apply animations based on action type
      if (playButtonRef.current) {
        if (action.type === "START_SPIN" && source === "click") {
          // Play both rotation and press animations for start spin (click/tap)
          createPlayButtonRotationAnimation(playButtonRef.current);
          createButtonPressAnimation(playButtonRef.current);
        } else if (action.type === "START_SPIN" && source === "spacebar") {
          // Only rotate for spacebar-triggered spins
          createPlayButtonRotationAnimation(playButtonRef.current);
        } else if (action.type === "STOP_SPIN") {
          // Press animation for stop action
          createButtonPressAnimation(playButtonRef.current);
        }
      }

      // Flash brightness for click/tap interactions
      if (source === "click") {
        flashBrightness();
      }

      switch (action.type) {
        case "STOP_SPIN":
          executeStopSpin();
          break;
        case "START_SPIN":
          executeStartSpin();
          break;
        default:
          // Unknown action type or disabled
          break;
      }
    },
    [executeStopSpin, executeStartSpin, playButtonRef, flashBrightness]
  );

  /**
   * Handle click function
   * If collector is showing, skip collection animation
   * Otherwise: start spin if not spinning, stop spin if spinning
   */
  const handleClick = useCallback(() => {
    const state = store.getState();

    // If collector is showing, skip collection animation
    if (showHoldAndWinCollector) {
      triggerCollectionSkip();
      return;
    }

    const isSpinning = state.spin.holdAndWinIsSpinning;

    // Check for critical animation (expanding wilds, etc.)
    if (state.animations.criticalAnimationPlaying) {
      return;
    }

    if (state.userInterface.userInterfaceDisabled) {
      return;
    }

    // Block clicks if stop button is disabled (after stop is pressed)
    if (stopButtonDisabledRef.current) {
      return;
    }

    // Allow clicks during spin (for manual stop), but block if not spinning and actionInProgress (prevent double-clicks)
    if (actionInProgressRef.current && !isSpinning) {
      return;
    }

    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {
      return;
    }

    const action = resolveButtonAction();
    executeButtonAction(action, "click");
  }, [
    showHoldAndWinCollector,
    triggerCollectionSkip,
    resolveButtonAction,
    executeButtonAction,
  ]);

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(
    HOLD_AND_WIN_PLAY_BUTTON_CONFIG,
    true
  );

  // Calculate responsive button position based on device and orientation
  // Uses toolbar width instead of full screen width
  const buttonPosition = (() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.65,
          y: -dimensions.height / 2 + buttonConfig.height / 2.0,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.4,
          y: toolbar.height - buttonConfig.height / 1.7,
        };
      }
    } else {
      const yOffset = 0.04;
      return {
        x: toolbar.width - buttonConfig.width * 1.275,
        y: uiSymbolSize * yOffset,
      };
    }
  })();

  // Check if button should be disabled
  // Allow clicks during spin for manual stop - only disable during transitions
  // IMPORTANT: Disable manual stop in turbo mode (turbo handles stops automatically)
  const isDisabled =
    userInterfaceDisabled ||
    (actionInProgress && !holdAndWinIsSpinning) ||
    stopButtonDisabled ||
    isTurboMode;

  // Get the appropriate icon texture - always use play icon
  const iconTexture = PIXI.Assets.get(ASSET_NAME.icon_play);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (!isDisabled && playButtonRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !playButtonRef.current.filters ||
        playButtonRef.current.filters.length === 0
      ) {
        playButtonRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [isDisabled, playButtonRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (playButtonRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      playButtonRef.current.filters = [];
    }
  }, [playButtonRef]);

  // Clean up hover effects when disabled state changes
  useEffect(() => {
    if (isDisabled && playButtonRef.current) {
      isHoveredRef.current = false;
      playButtonRef.current.filters = [];
    }
  }, [isDisabled, playButtonRef]);

  // Prevent context menu on long press/touch-and-hold
  useEffect(() => {
    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });
    return () =>
      document.removeEventListener("contextmenu", preventContextMenu);
  }, []);

  // Keyboard event handling - spacebar tap (no hold/turbo logic)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      event.preventDefault();

      const state = store.getState();

      // If collector is showing, skip collection animation
      if (showHoldAndWinCollector) {
        triggerCollectionSkip();
        spacePressed = false;
        return;
      }

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (state.userInterface.userInterfaceDisabled) {
        return;
      }

      // Block spacebar if stop button is disabled (after stop is pressed)
      if (stopButtonDisabledRef.current) {
        return;
      }

      // Allow spacebar during spin (for manual stop), but block if not spinning and actionInProgress
      const isSpinning = state.spin.holdAndWinIsSpinning;
      if (actionInProgressRef.current && !isSpinning) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      // Track spacebar press (simple tap only, no hold timer)
      if (!spacePressed) {
        spacePressed = true;
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      if (!spacePressed) return;

      const state = store.getState();

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        spacePressed = false;
        return;
      }

      // Block spacebar if stop button is disabled (after stop is pressed)
      if (stopButtonDisabledRef.current) {
        spacePressed = false;
        return;
      }

      // Allow spacebar during spin (for manual stop), but block if not spinning and actionInProgress
      const isSpinning = state.spin.holdAndWinIsSpinning;
      if (actionInProgressRef.current && !isSpinning) {
        spacePressed = false;
        return;
      }

      // Reset spacebar state
      spacePressed = false;

      // Execute action on spacebar release (simple tap)
      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) {
        return;
      }

      const action = resolveButtonAction();
      executeButtonAction(action, "spacebar");
    };

    const handleWindowBlur = () => {
      // Cleanup on window blur
      spacePressed = false;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      spacePressed = false;
    };
  }, [
    spacebarSpinEnabled,
    showHoldAndWinCollector,
    triggerCollectionSkip,
    resolveButtonAction,
    executeButtonAction,
  ]);

  return (
    <Sprite
      ref={playButtonRef}
      texture={iconTexture}
      name="hold_and_win_play_stop_button"
      anchor={0.5}
      width={buttonConfig.width}
      height={buttonConfig.height}
      x={buttonPosition.x}
      y={buttonPosition.y}
      eventMode={isDisabled ? "none" : "static"}
      cursor={isDisabled ? "default" : "pointer"}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
      onpointertap={handleClick}
    />
  );
};

export default ButtonHoldAndWinPlay;
